import React, { useRef } from 'react';
import { deleteImageExif } from 'utils/image';
import alertMessage from 'global/AlertMessage';
import ERROR_MESSAGE from 'constants/errorMessage';
import { IMAGE_TYPE_COMMON, MAX_FILE_SIZE_MB, MAX_IMAGE_SIZE_MB } from './constants';
import { IImageItem } from './types';

const DRAG_AREA_CLASSNAME = 'show-drag-area';
const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const DOCUMENT_TYPES = ['application/pdf'];
const MAX_IMAGE_LENGTH = 10;

const isType = (fileType, types) => types.includes(fileType.toLowerCase());
const isImage = (fileType) => isType(fileType, IMAGE_TYPES);
const isDocument = (fileType) => isType(fileType, DOCUMENT_TYPES);

const getImageDimensions = (file) => new Promise((resolved) => {
  const i = new Image();
  i.src = file;
  i.onload = () => {
    resolved({ width: i.width, height: i.height });
  };
});

function useDropFile({
  isAllowedToUpload,
  setDocuments,
  setImages,
  onErrorMaxSize,
  images,
  documents,
}) {
  const dropzoneRef = useRef<HTMLDivElement>();

  const handleOnDragStart = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (isAllowedToUpload) {
      const dropZone = dropzoneRef.current;
      dropZone.classList.add(DRAG_AREA_CLASSNAME);
    }
  };

  const handleOnDragOver = (e) => {
    e.preventDefault();
  };

  const handleOnDrop = async (e) => {
    e.preventDefault();
    const listDocuments = [...documents];
    const listImages = [...images];

    if (isAllowedToUpload) {
      const { dataTransfer } = e;
      dataTransfer.dropEffect = 'copy';

      const dropZone = dropzoneRef?.current;
      dropZone.classList.remove(DRAG_AREA_CLASSNAME);

      if (e.dataTransfer.files.length !== 0 && e.dataTransfer.items) {
        const arrayDataTransfer = Object.values(e.dataTransfer.items);
        const uploadedData = arrayDataTransfer.length > MAX_IMAGE_LENGTH
          ? arrayDataTransfer.slice(0, MAX_IMAGE_LENGTH)
          : arrayDataTransfer;

        // for (const item of e.dataTransfer.items) {
        // eslint-disable-next-line array-callback-return
        const promisesList = [];
        uploadedData.map(async (item) => {
          if (item) {
            const process = new Promise((resolve, reject) => {
              const readerBuffer = new FileReader();
              // @ts-ignore
              const file = item.getAsFile();

              const { type: fileType, size } = file;
              const fileSize = size / 1024 / 1024;

              readerBuffer.readAsArrayBuffer(file);

              readerBuffer.onloadend = async (event) => {
                if (isImage(fileType)) {
                  if (fileSize > MAX_IMAGE_SIZE_MB) {
                    onErrorMaxSize(true);
                  } else {
                    const isImageNeverAdded = !(listImages
                      .findIndex((image) => image.fileName === file?.name) > -1);
                    if (isImageNeverAdded) {
                      const imagePreviewUrl = await URL.createObjectURL(file);

                      const dimensions = await getImageDimensions(imagePreviewUrl);

                      const imageData: IImageItem = {
                        isNew: true,
                        type: IMAGE_TYPE_COMMON,
                        imagePreviewUrl,
                        dimensions,
                        fileName: file?.name,
                      };
                      if (fileType === 'image/jpeg' || fileType === 'image/jpg') {
                        const deletedExif = await deleteImageExif(event.target.result, fileType);
                        try {
                          // @ts-ignore
                          imageData.file = deletedExif;
                        } catch (err) {
                          imageData.file = file;
                        }
                      } else {
                        imageData.file = file;
                      }
                      // onChangeImage(imageData);
                      resolve({ type: 'image', data: imageData });
                    }
                  }
                } else if (isDocument(fileType)) {
                  if (fileSize > MAX_FILE_SIZE_MB) {
                    reject(new Error(`File too large. The maximum size is ${MAX_FILE_SIZE_MB}MB`));
                  } else {
                    const isFileNeverAdded = !(listDocuments
                      .findIndex((document) => document.name === file?.name) > -1);
                    if (isFileNeverAdded) {
                      // onChangeDocument(file);
                      resolve({ type: 'document', data: file });
                    }
                  }
                }
              };
            });

            promisesList.push(process);
          }
        });

        Promise.all(promisesList)
          .then((res) => {
            // eslint-disable-next-line array-callback-return
            res.map((item) => {
              if (item.type === 'image') {
                listImages.push(item.data);
              }
              if (item.type === 'document') {
                if (listDocuments.length < 1) {
                  listDocuments.push(item.data);
                } else {
                  throw new Error(ERROR_MESSAGE.MAX_UPLOAD_FILES);
                }
              }
            });
          })
          .then(() => {
            setDocuments(listDocuments);
            if (listImages.length < 10) {
              setImages(listImages);
            }
          })
          .catch((err) => {
            alertMessage({
              content: err.message,
              alertType: 'plain-default',
              messageType: 'error',
              hasCloseIcon: false,
            });
          });
      }
    }
  };

  return {
    dropzoneRef,
    handleOnDragStart,
    handleOnDragOver,
    handleOnDrop,
  };
}

export default useDropFile;
